/**
 * trefferNavigation — wohin ein Suchtreffer fuehrt, an EINER Stelle.
 *
 * @description
 *   Die Trefferliste (`SidebarAndHeaderBar.jsx`, Desktop und Mobil) rief bisher
 *   `navigate(r.href)` — React-Router. Seit Stufe N2 traegt die Sicht fuer
 *   Bestellungen einen **externen** Verweis: `shopify-order:<order_id>`. Das ist
 *   ein erkennbarer Marker, KEINE URL. Die Admin-Adresse baut das Frontend aus
 *   `shopifyAdmin.js::shopifyOrderUrl` — dort liegt die Shop-Konfiguration
 *   (`REACT_APP_SHOPIFY_ADMIN_URL`/`_STORE`). Eine in SQL gebaute URL waere ein
 *   zweiter Ort fuer dieselbe Adresse und wuerde den Shop-Namen in die Datenbank
 *   schreiben.
 *
 *   🔴 **Warum ein Marker und nicht eine leere `href` + `extra.order_id`.** Der
 *   Klick-Handler muss sonst die ART kennen (`r.type === 'order'`) — eine zweite
 *   Regel neben dem Verweis. Der Marker traegt die Entscheidung in der Zeile
 *   selbst: „extern, und zwar eine Bestellung". Rechnungen bleiben intern
 *   (`/invoicing/<id>`) und laufen unveraendert ueber den Router.
 *
 * @useWhen
 *   - Ein Suchtreffer wird angeklickt oder mit Enter geoeffnet
 *   - Eine neue Trefferart bekommt einen externen Verweis
 *
 * @dontUseFor
 *   - Andere externe Links (Kunden, Produkte): die haben im Treffer einen
 *     internen Pfad und oeffnen den Admin-Link auf der Zielseite
 *
 * @related
 *   - `utils/shopifyAdmin.js` — die EINE Quelle der Admin-Adresse
 *   - `components/common/SearchResultItem.jsx` — die Zeile, die fuer einen
 *     externen Marker ein echtes `<a href>` rendert
 */

import { shopifyOrderUrl } from './shopifyAdmin'

/** Der Marker, den `backend/migrations/416_suche_art_bestellungen.sql` setzt. */
export const SHOPIFY_ORDER_PREFIX = 'shopify-order:'

/**
 * Die externe Ziel-URL eines Treffers — oder `null`, wenn er intern ist.
 *
 * @param {string|null|undefined} href der `href` der Sichtzeile
 * @returns {string|null}
 */
export function externesZiel(href) {
  if (typeof href !== 'string' || !href.startsWith(SHOPIFY_ORDER_PREFIX)) {
    return null
  }
  return shopifyOrderUrl(href.slice(SHOPIFY_ORDER_PREFIX.length))
}

/**
 * Einen Treffer oeffnen: extern ist die ZEILE der Link, intern der Router.
 *
 * 🔴 **Seit der Regel-16-Nachbesserung 1 (2026-10-19) oeffnet diese Funktion
 * KEIN Fenster mehr.** `SearchResultItem` rendert fuer einen externen Marker
 * ein echtes `<a href=… target="_blank" rel="noopener noreferrer">`; der
 * Browser oeffnet den Tab, und ein Popup-Blocker hat keinen Angriffspunkt
 * (das war der stille tote Klick aus R16-W3). Hier bleibt nur die
 * ENTSCHEIDUNG und der Innenweg.
 *
 * 🔴 **Ein Marker OHNE Kennung navigiert NICHT.** `shopifyOrderUrl('')` liefert
 * `null`; ein `navigate('shopify-order:')` liefe in den SPA-Catch-all und waere
 * ein toter Klick. Die Funktion meldet den Fall mit `'leer'` zurueck, statt ihn
 * zu verstecken.
 *
 * @param {string} href
 * @param {(ziel: string) => void} navigate der React-Router-Navigator
 * @returns {'extern'|'intern'|'leer'}
 */
export function oeffneTreffer(href, navigate) {
  if (typeof href === 'string' && href.startsWith(SHOPIFY_ORDER_PREFIX)) {
    return externesZiel(href) ? 'extern' : 'leer'
  }
  navigate(href)
  return 'intern'
}
